/**
 * Phase 26 Part 2 — Account page.
 *
 * Shows current plan, promo-code redemption, and (placeholder) paid-plan
 * upgrade copy. No billing UI yet; that arrives with the Stripe handler.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useAppModeStore, type DefaultMode } from '../store/useAppModeStore';
import {
  getPromoStatus,
  redeemPromoCode,
  type Tier,
} from '../utils/api';

const TIER_LABEL: Record<Tier, string> = {
  free: 'Free',
  promo: 'Promo (Fleet beta)',
  paid: 'Paid (Fleet)',
};

const PLAN_ROWS: { tier: Tier; label: string; blurb: string }[] = [
  { tier: 'free', label: 'Free', blurb: 'Personal TCO — unlimited scenarios.' },
  { tier: 'promo', label: 'Promo', blurb: 'Full fleet access (beta).' },
  { tier: 'paid', label: 'Paid', blurb: 'Full fleet access (coming soon).' },
];

export default function AccountPage() {
  const { user, signOut } = useAuthStore();
  const navigate = useNavigate();
  const tier = useAppModeStore((s) => s.tier);
  const initTier = useAppModeStore((s) => s.initTier);
  const defaultMode = useAppModeStore((s) => s.defaultMode);
  const setDefaultMode = useAppModeStore((s) => s.setDefaultMode);
  const isFleetTierActive = useAppModeStore((s) => s.isFleetTierActive);

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [redeemedCodes, setRedeemedCodes] = useState<string[]>([]);
  const [statusLoading, setStatusLoading] = useState(true);
  // UX audit fix: getPromoStatus is the authoritative tier source for this page.
  // If it fails we genuinely don't know the plan — track that so we render an
  // "unavailable / retry" state instead of asserting the fallback tier as fact.
  const [statusError, setStatusError] = useState(false);
  const [defaultModeSaved, setDefaultModeSaved] = useState(false);

  async function handleDefaultModeChange(mode: DefaultMode) {
    await setDefaultMode(mode);
    setDefaultModeSaved(true);
    window.setTimeout(() => setDefaultModeSaved(false), 1500);
  }

  const loadStatus = useCallback(() => {
    let cancelled = false;
    setStatusLoading(true);
    setStatusError(false);
    getPromoStatus()
      .then((s) => {
        if (cancelled) return;
        initTier(s.tier);
        setRedeemedCodes(s.redeemed_codes);
      })
      .catch(() => {
        // Could not reach the server — the plan is UNKNOWN, not free. Flag it so
        // the UI shows a retry rather than rendering the fallback tier as fact.
        if (!cancelled) setStatusError(true);
      })
      .finally(() => {
        if (!cancelled) setStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initTier]);

  useEffect(() => loadStatus(), [loadStatus]);

  async function handleRedeem(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Enter a code to continue.');
      return;
    }
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const res = await redeemPromoCode(trimmed);
      initTier(res.tier);
      setRedeemedCodes((prev) => Array.from(new Set([...prev, trimmed])));
      setCode('');
      setSuccess(
        res.tier === 'free'
          ? 'Code accepted, but no tier change applied.'
          : 'Access granted. Welcome to Fleet TCO.',
      );
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Invalid or expired code.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    navigate('/login', { replace: true });
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px' }}>
      <h1
        style={{
          fontSize: 24,
          fontWeight: 600,
          marginBottom: 24,
          color: 'var(--tco-ink)',
        }}
      >
        Account
      </h1>

      <section className="cnc-panel rounded-xl p-6 mb-6">
        <h2 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, color: 'var(--tco-ink-2)' }}>
          Profile
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, fontSize: 14 }}>
          <div style={{ color: 'var(--tco-ink-2)' }}>Email</div>
          <div style={{ color: 'var(--tco-ink)' }}>{user?.email ?? '—'}</div>
          <div style={{ color: 'var(--tco-ink-2)' }}>Current plan</div>
          <div style={{ color: 'var(--tco-ink)', fontWeight: 600 }}>
            {statusLoading ? (
              <span style={{ color: 'var(--tco-ink-3)', fontWeight: 400 }}>Loading…</span>
            ) : statusError ? (
              <span>
                <span style={{ color: 'var(--tco-ink-3)', fontWeight: 400 }}>
                  Unavailable — couldn't reach the server.
                </span>{' '}
                <button
                  type="button"
                  onClick={() => loadStatus()}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--tco-neg)',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                    padding: 0,
                    fontFamily: 'inherit',
                  }}
                >
                  Retry
                </button>
              </span>
            ) : (
              TIER_LABEL[tier]
            )}
          </div>
        </div>
      </section>

      <section className="cnc-panel rounded-xl p-6 mb-6">
        <h2 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, color: 'var(--tco-ink-2)' }}>
          Default mode
        </h2>
        <p style={{ fontSize: 13, color: 'var(--tco-ink-2)', marginBottom: 12 }}>
          When you log in, open:
        </p>
        <div role="radiogroup" aria-label="Default mode" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <DefaultModeRadio
            value="individual"
            label="Personal mode"
            current={defaultMode}
            onChange={handleDefaultModeChange}
            disabled={false}
          />
          <DefaultModeRadio
            value="fleet"
            label={isFleetTierActive ? 'Fleet mode' : 'Fleet mode (requires fleet access)'}
            current={defaultMode}
            onChange={handleDefaultModeChange}
            disabled={!isFleetTierActive}
          />
          <DefaultModeRadio
            value="ask"
            label="Ask me each time"
            current={defaultMode}
            onChange={handleDefaultModeChange}
            disabled={false}
          />
        </div>
        {defaultModeSaved && (
          <p style={{ color: '#4ade80', fontSize: 12, marginTop: 10 }} role="status">
            Saved
          </p>
        )}
      </section>

      <section className="cnc-panel rounded-xl p-6 mb-6">
        <h2 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, color: 'var(--tco-ink-2)' }}>
          Plan details
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', rowGap: 8, fontSize: 14 }}>
          {PLAN_ROWS.map((r) => (
            // Don't mark a plan "CURRENT" when we couldn't load the real tier.
            <Row key={r.tier} label={r.label} blurb={r.blurb} active={!statusError && r.tier === tier} />
          ))}
        </div>
        <p style={{ marginTop: 16, fontSize: 12, color: 'var(--tco-ink-3)' }}>
          Paid plans coming soon.
        </p>
      </section>

      <section className="cnc-panel rounded-xl p-6 mb-6">
        <h2 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, color: 'var(--tco-ink-2)' }}>
          Promo code
        </h2>
        <form onSubmit={handleRedeem} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter code"
            disabled={submitting}
            style={{
              flex: 1,
              padding: '8px 12px',
              fontSize: 14,
              borderRadius: 6,
              border: '1px solid var(--tco-line)',
              background: 'var(--tco-bg)',
              color: 'var(--tco-ink)',
            }}
            aria-invalid={!!error}
          />
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary"
            style={{
              padding: '8px 16px',
              fontSize: 14,
              borderRadius: 6,
            }}
          >
            {submitting ? 'Redeeming…' : 'Redeem'}
          </button>
        </form>
        {error && (
          <p style={{ color: '#f87171', fontSize: 13, marginTop: 8 }} role="alert">
            {error}
          </p>
        )}
        {success && (
          <p style={{ color: '#4ade80', fontSize: 13, marginTop: 8 }} role="status">
            {success}
          </p>
        )}
        {!statusLoading && redeemedCodes.length > 0 && (
          <div style={{ marginTop: 16, fontSize: 13, color: 'var(--tco-ink-2)' }}>
            Redeemed:{' '}
            {redeemedCodes.map((c, i) => (
              <span key={c}>
                <code style={{ fontFamily: 'monospace' }}>{c}</code>
                {i < redeemedCodes.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        )}
      </section>

      <section className="cnc-panel rounded-xl p-6">
        <h2 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, color: 'var(--tco-ink-2)' }}>
          Danger zone
        </h2>
        <button
          onClick={handleSignOut}
          className="btn-secondary"
          style={{ padding: '8px 16px', fontSize: 14, borderRadius: 6 }}
        >
          Sign out
        </button>
      </section>
    </div>
  );
}

function DefaultModeRadio({
  value,
  label,
  current,
  onChange,
  disabled,
}: {
  value: DefaultMode;
  label: string;
  current: DefaultMode;
  onChange: (v: DefaultMode) => void;
  disabled: boolean;
}) {
  const checked = current === value;
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: disabled ? 'var(--tco-ink-3)' : 'var(--tco-ink)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <input
        type="radio"
        name="default-mode"
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
      />
      {label}
    </label>
  );
}

function Row({ label, blurb, active }: { label: string; blurb: string; active: boolean }) {
  return (
    <>
      <div style={{ color: active ? 'var(--tco-ink)' : 'var(--tco-ink-2)', fontWeight: active ? 600 : 400 }}>
        {label}
        {active && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 11,
              padding: '2px 6px',
              borderRadius: 4,
              background: 'var(--tco-ink)',
              color: 'var(--tco-bg)',
            }}
          >
            CURRENT
          </span>
        )}
      </div>
      <div style={{ color: 'var(--tco-ink-2)' }}>{blurb}</div>
    </>
  );
}
